import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  ChangeDetectorRef,
  DestroyRef,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SidebarLinkComponent } from '../sidebar-link/sidebar-link.component';
import { UserProfileService } from '../../../../services/user/user-profile.service';
import { SettingsService } from '../../../../services/settings/settings.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd, RouterLink } from '@angular/router';
import { filter } from 'rxjs';
import { formatCountdown } from '../../../../../utils/sms';

/** The soonest scheduled send, kept in view on every page but the dashboard. */
export interface SidebarNextSend {
  subject: string;
  at: Date;
}

export interface ISidebarLink {
  iconName: string;
  title: string;
  link?: string;
  children?: ISidebarLink[];
  /** A small count beside the link; hidden when 0 or missing. */
  badge?: number;
  /** What the count means, for tooltips and screen readers. */
  badgeLabel?: string;
}

/**
 * The console's left rail. Top-level groups (SMS, USSD) are channels: tabs
 * pick one and the rail lists only that channel's tools, so the nav never
 * nests. The tabs follow the route, so deep links and the back button land
 * on the right channel. The current page is a solid pill that slides between
 * links, and the next scheduled send counts down at the foot.
 */
@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [SidebarLinkComponent, MatTooltipModule, CommonModule, RouterLink],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css',
})
export class SidebarComponent implements OnInit, OnChanges, OnDestroy {
  userProfileService = inject(UserProfileService);
  settingsService = inject(SettingsService);
  cdr = inject(ChangeDetectorRef);
  router = inject(Router);
  private destroyRef = inject(DestroyRef);

  @Input() links: ISidebarLink[] = [];
  @Input() isMobileOpen: boolean = false;
  @Input() nextSend: SidebarNextSend | null = null;
  @Output() nextSendOpen = new EventEmitter<void>();

  institutionLogo: string | null = null;
  isCollapsed: boolean = false;
  /** Turns on the width transition once the restored state has painted. */
  isReady = false;
  selectedChannel = '';

  /** Icons for the channel switch, keyed by group title. */
  private readonly channelIcons: Record<string, string> = {
    SMS: 'sms',
    USSD: 'dialpad',
  };

  get userDetails() {
    return this.userProfileService.userDetails;
  }

  get OrganizationName() {
    const organization = this.userProfileService.userDetails?.organization;
    if (!organization) return '';
    return Object.keys(organization)[0] ?? '';
  }

  /** Up to two initials for the organisation tile when no logo is set. */
  get orgInitials(): string {
    const words = this.OrganizationName.split(/\s+/).filter(Boolean);
    const initials = words
      .filter((word) => /^[A-Za-z0-9]/.test(word))
      .slice(0, 2)
      .map((word) => word[0].toUpperCase())
      .join('');
    return initials || 'B';
  }

  get channels(): ISidebarLink[] {
    return this.links.filter((item) => this.isGroup(item));
  }

  /** Top-level links that are not channels render under the channel list. */
  get looseLinks(): ISidebarLink[] {
    return this.links.filter((item) => !this.isGroup(item));
  }

  get currentChannel(): ISidebarLink | undefined {
    return (
      this.channels.find((channel) => channel.title === this.selectedChannel) ??
      this.channels[0]
    );
  }

  get channelIndex(): number {
    const index = this.channels.findIndex(
      (channel) => channel.title === this.currentChannel?.title
    );
    return Math.max(0, index);
  }

  /**
   * Where the pill sits: the current channel's link for this page, by its
   * longest matching path (as routerLinkActive matches), or -1 for none.
   */
  get activeIndex(): number {
    const url = this.router.url.split(/[?#]/)[0];
    let best = -1;
    let bestLength = 0;
    (this.currentChannel?.children ?? []).forEach((item, index) => {
      const link = item.link ?? '';
      const matches = !!link && (url === link || url.startsWith(`${link}/`));
      if (matches && link.length > bestLength) {
        best = index;
        bestLength = link.length;
      }
    });
    return best;
  }

  /** The dashboard shows the queue itself, so the rail's countdown stands down there. */
  get showNextSend(): boolean {
    return !!this.nextSend && !this.router.url.startsWith('/organization/dashboard');
  }

  get nextCountdown(): string {
    return this.nextSend ? formatCountdown(this.nextSend.at, new Date()) : '';
  }

  /** The countdown without "in" and with hours shortened ("1h 35m"), to fit under the collapsed rail's icon. */
  get nextCountdownShort(): string {
    return this.nextCountdown
      .replace(/^in /, '')
      .replace(/^(\d+) h(?: (\d+) min)?$/, (_, hours, minutes) => (minutes ? `${hours}h ${minutes}m` : `${hours}h`));
  }

  get nextTooltip(): string {
    return this.nextSend ? `Next send ${this.nextCountdown}: ${this.nextSend.subject}` : '';
  }

  channelIcon(channel: ISidebarLink): string {
    return this.channelIcons[channel.title] ?? channel.iconName;
  }

  isGroup(item: ISidebarLink): boolean {
    return !!item.children?.length;
  }

  isChildActive(item: ISidebarLink): boolean {
    if (!item.children) return false;
    return item.children.some(
      (child) => child.link && this.router.url.startsWith(child.link)
    );
  }

  selectChannel(title: string) {
    this.selectedChannel = title;
  }

  /** Arrow keys move between channels, as in any radio group. */
  stepChannel(event: Event, delta: number) {
    event.preventDefault();
    const count = this.channels.length;
    if (!count) return;
    const next = (this.channelIndex + delta + count) % count;
    this.selectedChannel = this.channels[next].title;
    const group = (event.currentTarget as HTMLElement).closest(
      '[role="radiogroup"]'
    );
    const buttons = group?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
    buttons?.[next]?.focus();
  }

  private syncChannelToRoute() {
    const match = this.channels.find((channel) => this.isChildActive(channel));
    if (match) this.selectedChannel = match.title;
  }

  fetchOrganizationLogo() {
    this.settingsService.getOrganizationLogo().subscribe({
      error: () => {},
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // Links are rebuilt when roles load and whenever a badge count changes.
    // Follow the route only while no channel is chosen or the chosen one is
    // gone, so a channel picked by hand is not reset mid-browse.
    if (!changes['links']) return;
    const known = this.channels.some((channel) => channel.title === this.selectedChannel);
    if (!known) this.syncChannelToRoute();
  }

  ngOnInit() {
    this.settingsService.getOrganizationLogoData
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((logoData) => {
        if (this.institutionLogo && this.institutionLogo.startsWith('blob:')) {
          URL.revokeObjectURL(this.institutionLogo);
        }

        if (logoData) {
          this.institutionLogo = logoData;
        } else if (logoData === null) {
          this.institutionLogo = null;
        }

        this.cdr.detectChanges();
      });

    this.fetchOrganizationLogo();

    if (typeof localStorage !== 'undefined') {
      const savedState = localStorage.getItem('sidebarCollapsed');
      if (savedState !== null) {
        this.isCollapsed = savedState === 'true';
      }
      // Enable transitions after the restored width has been painted.
      setTimeout(() => (this.isReady = true), 300);
    }

    this.syncChannelToRoute();

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.syncChannelToRoute());
  }

  toggleSidebar() {
    this.isCollapsed = !this.isCollapsed;
    localStorage.setItem('sidebarCollapsed', this.isCollapsed.toString());
  }

  ngOnDestroy() {
    if (this.institutionLogo && this.institutionLogo.startsWith('blob:')) {
      URL.revokeObjectURL(this.institutionLogo);
    }
  }
}
